/**
 * Savage Worlds initiative deck — pure, DOM-free, unit-tested.
 *
 * 54 cards: ranks 2..14 (J=11, Q=12, K=13, A=14) × 4 suits, plus a red and a
 * black joker.
 *
 * Initiative order (acts first = "greater"):
 *   1. Jokers beat everything: red joker > black joker
 *   2. Then by rank descending: Ace (14) → 2
 *   3. Tie-break within a rank by suit: spade > heart > diamond > club
 *
 * Within a single 54-card deck every dealt card is unique, so the comparator
 * is a strict total order. `compareCards(a, b)` returns negative when `a`
 * acts before `b` (so Array.sort gives initiative order directly).
 */

import type { Card, Suit } from './types.js';

const SUITS: readonly Suit[] = ['spade', 'heart', 'diamond', 'club'];
const SUIT_RANK: Record<Suit, number> = { spade: 3, heart: 2, diamond: 1, club: 0 };
const RANK_MIN = 2;
const RANK_MAX = 14; // Ace

export function buildDeck(): Card[] {
  const deck: Card[] = [];
  for (let rank = RANK_MIN; rank <= RANK_MAX; rank++) {
    for (const suit of SUITS) {
      deck.push({ kind: 'rank', rank, suit });
    }
  }
  deck.push({ kind: 'joker', color: 'red' });
  deck.push({ kind: 'joker', color: 'black' });
  return deck;
}

/**
 * Fisher–Yates shuffle. Returns a new array (does not mutate input).
 * `rng` defaults to Math.random; tests inject a deterministic generator.
 */
export function shuffle<T>(items: readonly T[], rng: () => number = Math.random): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}

/** Numeric "strength" — higher acts earlier. Jokers tower above ranks. */
function cardStrength(card: Card): number {
  if (card.kind === 'joker') {
    // Far above any rank card. Red just edges out black.
    return card.color === 'red' ? 100_001 : 100_000;
  }
  // rank (2..14) major, suit (0..3) minor tiebreak.
  return card.rank * 10 + SUIT_RANK[card.suit];
}

/**
 * Comparator for Array.prototype.sort producing initiative order
 * (first to act first). Negative ⇒ `a` acts before `b`.
 */
export function compareCards(a: Card, b: Card): number {
  return cardStrength(b) - cardStrength(a);
}

/** Stable, human-readable id for a card (used for dedupe + tests). */
export function cardId(card: Card): string {
  return card.kind === 'joker' ? `joker-${card.color}` : `${card.rank}-${card.suit}`;
}
